import { v4 } from 'uuid'
const { subtle } = window.crypto

type TKeyPair = { privateKey?: any; publicKey?: any }

type TKeyExportFormat = 'jwk' | 'pkcs8' | 'spki' | 'raw'

const DPOP_CONFIGS = (() => {
	const localStorageKeys = {
		private: 'privateKey',
		public: 'publicKey',
	}

	const keyUsages = ['sign', 'verify']

	const exportKeyInFormat: TKeyExportFormat = 'jwk'

	const defaultOptions = {
		name: 'ECDSA',
		namedCurve: 'P-256',
		hash: {
			name: 'SHA-256',
		},
	}

	return {
		localStorageKeys,
		keyUsages,
		exportKeyInFormat,
		defaultOptions,
	}
})()

function replaceCharacterAtIndex(str: string, index: number, newChar: string) {
	if (index < 0 || index >= str.length || newChar.length !== 1) {
		return str // Nếu index không hợp lệ hoặc ký tự mới không phải là một ký tự đơn, trả về chuỗi ban đầu
	}
	return str.slice(0, index) + newChar + str.slice(index + 1)
}

const getRandomInt = (min: number, max: number) => {
	min = Math.ceil(min)
	max = Math.floor(max)
	return Math.floor(Math.random() * (max - min + 1)) + min
}

const encodeJTI = (origin: string) => {
	const component = origin.split('-')

	const initValue: { signature: string[]; encoded: string[] } = {
		signature: [],
		encoded: [],
	}

	const { signature, encoded }: { signature: string[]; encoded: string[] } =
		component
			.map((x) => {
				const index = getRandomInt(0, x.length - 1)
				const chartCodeAtIndex = x.charCodeAt(index)
				const newStr = replaceCharacterAtIndex(x, index, 'x')

				return {
					index,
					chartCodeAtIndex,
					newStr,
				}
			})
			.reduce((res, current) => {
				const iLength = `${current.index.toString().length}`.padStart(
					2,
					'0'
				)
				const ccLength = `${
					current.chartCodeAtIndex.toString().length
				}`.padStart(2, '0')
				res.signature.push(
					[
						iLength,
						current.index,
						ccLength,
						current.chartCodeAtIndex,
					].join('')
				)
				res.encoded.push(current.newStr)

				return res
			}, initValue)

	return {
		origin,
		signature: signature.join(''),
		encoded: encoded.join('-'),
	}
}

const encodeBase64Url = (e: ArrayBuffer | Uint8Array): string => {
	const uint8 = e instanceof Uint8Array ? e : new Uint8Array(e)
	const binary = Array.from(uint8, (byte) => String.fromCharCode(byte)).join(
		''
	)
	return btoa(binary)
		.replace(/=/g, '')
		.replace(/\+/g, '-')
		.replace(/\//g, '_')
}

const encodeJwtInfo = function (e: string) {
	return new TextEncoder().encode(unescape(encodeURIComponent(e)))
}

const getOrigin = () => window.location.origin

const startsWithSchema = (url: string) => /^http(s)?:\/\//.test(url)

const getPath = function (url: string) {
	try {
		if (startsWithSchema(url)) {
			const { pathname } = new URL(url)
			return pathname
		} else {
			const { pathname } = new URL(url, getOrigin())
			return pathname
		}
	} catch {
		return url
	}
}

const createDopHeader = async function (url: string, method: string = 'GET') {
	url = getPath(url)

	const { privateKey: privateJsonWebKey } = await getKeys()

	const { crv, kty, x, y } = privateJsonWebKey

	const key = await subtle.importKey(
		DPOP_CONFIGS.exportKeyInFormat,
		privateJsonWebKey,
		{
			name: 'ECDSA',
			namedCurve: 'P-256',
		},
		!0,
		[DPOP_CONFIGS.keyUsages[0] as any]
	)

	const header = JSON.stringify({
		// typ: "dpop+jwt",
		typ: 'dpop',
		alg: 'ES256',
		[DPOP_CONFIGS.exportKeyInFormat]: {
			crv,
			kty,
			x,
			y,
		},
	})

	const { encoded, signature: jti_sign } = encodeJTI(v4())

	const payload = JSON.stringify({
		iat: Math.ceil(new Date().getTime() / 1000),
		jti: encoded,
		htu: url,
		htm: method,
		jis: jti_sign,
	})
	const jwtWrap = [
		encodeBase64Url(encodeJwtInfo(header)),
		encodeBase64Url(encodeJwtInfo(payload)),
	].join('.')
	const encodedData = encodeJwtInfo(jwtWrap)
	const signature = await subtle.sign(
		{
			name: 'ECDSA',
			hash: {
				name: 'SHA-256',
			},
		},
		key,
		encodedData
	)
	return jwtWrap + '.' + encodeBase64Url(signature)
}

const getKeys = async () => {
	const pm = new Promise((resolve) => {
		return (async () => {
			if (
				localStorage.getItem(DPOP_CONFIGS.localStorageKeys.private) &&
				localStorage.getItem(DPOP_CONFIGS.localStorageKeys.public)
			) {
				return resolve(loadFromLocalStorage())
			}

			const { privateKey, publicKey } =
				await window.crypto.subtle.generateKey(
					DPOP_CONFIGS.defaultOptions,
					!0,
					DPOP_CONFIGS.keyUsages as KeyUsage[]
				)

			await saveToLocalStorage({ privateKey, publicKey })

			return resolve(loadFromLocalStorage())
		})()
	})

	return (await pm) as TKeyPair
}

const loadFromLocalStorage = (): TKeyPair => {
	try {
		const _privateKey = localStorage.getItem(
			DPOP_CONFIGS.localStorageKeys.private
		)
		const _publicKey = localStorage.getItem(
			DPOP_CONFIGS.localStorageKeys.public
		)
		return {
			privateKey: _privateKey ? JSON.parse(_privateKey) : undefined,
			publicKey: _publicKey ? JSON.parse(_publicKey) : undefined,
		}
	} catch (error) {
		console.error(error)
		return {}
	}
}

const saveToLocalStorage = async ({ publicKey, privateKey }: any) => {
	const publicKeyJSON = await window.crypto.subtle.exportKey(
		DPOP_CONFIGS.exportKeyInFormat,
		publicKey
	)

	const privateKeyJSON = await window.crypto.subtle.exportKey(
		DPOP_CONFIGS.exportKeyInFormat,
		privateKey
	)

	localStorage.setItem(
		DPOP_CONFIGS.localStorageKeys.private,
		JSON.stringify(privateKeyJSON)
	)

	localStorage.setItem(
		DPOP_CONFIGS.localStorageKeys.public,
		JSON.stringify(publicKeyJSON)
	)
}

export default createDopHeader
